// ✅ Static metadata export (used in project listing page)
export const metadata = {
  title: 'Luxury Pool, Spa & Outdoor Living in Porter Ranch, CA 91326',
  description:
    'A luxury outdoor living renovation in Porter Ranch featuring custom pool, spa, artificial turf, elegant hardscaping, and resort-style design by Modern Build.',
  link: '/services/outdoor-living',
  height: 'h-[467px]',
  image:
    '/projects/luxury-pool-spa-artificial-turf-hardscaping-in-porter-ranch-ca-91326/luxury-pool-spa-artificial-turf-hardscaping-in-porter-ranch-ca-91326.webp',
};

import fs from 'fs';
import path from 'path';
import Head from 'next/head';
import Header from '../../../components/Header';
import Hero from '../../../components/Hero';
import ContentBlock from '../../../components/ContentBlock';
import { VideoSection } from '../../../components/Videos';
import Gallery from '../../../components/Gallery';
import SocialProof from '../../../components/SocialProof';
import ContactUs from '../../../components/ContactUs';
import Footer from '../../../components/Footer';
import { generateWebPageSchema } from '../../../utils/generateWebPageSchema';

export async function getStaticProps() {
  const slug = 'luxury-pool-spa-artificial-turf-hardscaping-in-porter-ranch-ca-91326';

  const galleryPath = path.join(
    process.cwd(),
    'public',
    'projects',
    slug,
    'gallery'
  );

  let galleryImages = [];

  // ✅ Load gallery images
  if (fs.existsSync(galleryPath)) {
    const files = fs.readdirSync(galleryPath).filter(file =>
      /\.(jpg|jpeg|png|webp)$/i.test(file)
    );

    galleryImages = files.map(file => ({
      src: `/projects/${slug}/gallery/${file}`,
      alt: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
      title: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
    }));
  }

  return {
    props: {
      galleryImages,
    },
  };
}

export default function LuxuryPoolSpaPorterRanch({
  galleryImages,
}) {
  const meta = metadata;

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta name="author" content="Modern Build Home Remodeling" />

        <meta
          name="keywords"
          content="luxury pool Porter Ranch, spa outdoor living 91326, artificial turf backyard, hardscaping Porter Ranch, outdoor entertainment space Los Angeles, Modern Build"
        />

        <link
          rel="canonical"
          href="https://modernbuildremodel.com/projects/luxury-pool-spa-artificial-turf-hardscaping-in-porter-ranch-ca-91326"
        />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta
          property="og:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/projects/luxury-pool-spa-artificial-turf-hardscaping-in-porter-ranch-ca-91326"
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:site_name"
          content="Modern Build Home Remodeling"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={meta.title}
        />
        <meta
          name="twitter:description"
          content={meta.description}
        />
        <meta
          name="twitter:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />

        {/* Breadcrumb Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://modernbuildremodel.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Projects',
                  item: 'https://modernbuildremodel.com/projects',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name:
                    'Luxury Pool, Spa & Outdoor Living in Porter Ranch CA 91326',
                  item:
                    'https://modernbuildremodel.com/projects/luxury-pool-spa-artificial-turf-hardscaping-in-porter-ranch-ca-91326',
                },
              ],
            }),
          }}
        />

        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title:
                  'Luxury Pool, Spa & Outdoor Living in Porter Ranch, CA 91326 | Modern Build',
                description:
                  'Explore a luxury outdoor living renovation in Porter Ranch featuring a custom pool, spa, artificial turf and premium hardscaping.',
                slug:
                  '/projects/luxury-pool-spa-artificial-turf-hardscaping-in-porter-ranch-ca-91326',
              })
            ),
          }}
        />
      </Head>

      <Header />

      <Hero
        category="luxuryPoolSpaPorterRanchCA91326"
        page="page"
        hasContentH1={false}
      />

      <ContentBlock
        subtitle="PROJECT OVERVIEW"
        title="Luxury Pool, Spa & Outdoor Living in Porter Ranch"
        description="This luxury outdoor living renovation in Porter Ranch transformed an ordinary backyard into a resort-like retreat with pool, spa, artificial turf and beautifully integrated hardscape."
        extraText="Every element was crafted to enhance comfort, usability and aesthetic impact."
        buttonText="Plan My Outdoor Project"
        buttonLink="/services/outdoor-living"
      />

      <Gallery images={galleryImages} />

      <section className="bg-white py-[120px]">
        <div className="container mx-auto px-6">
          <VideoSection
            videoUrl="https://www.youtube.com/embed/AruuM8kGm2c"
            serviceType="Outdoor Living Renovation"
            location="Porter Ranch, CA"
            description="Watch how this luxury pool, spa and outdoor living space was brought to life in Porter Ranch."
            serviceLink="/services/outdoor-living"
            coverImage={meta.image}
            isVideoLeft={true}
          />
        </div>
      </section>

      <section className="bg-white py-[120px]">
  <div className="container mx-auto px-6 max-w-4xl">

    <h2 className="text-2xl font-light text-primary leading-tight font-gilda mb-4">
      Luxury Pool, Spa & Outdoor Living in Porter Ranch, CA 91326
    </h2>

    <p className="text-gray font-light text-lg leading-relaxed">
      This high-end outdoor living renovation in Porter Ranch, CA 91326 shows how thoughtful design can turn an everyday backyard into a private resort-style environment for family and guests to enjoy year-round.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      The design includes a custom pool and spa, <a
        href="https://modernbuildremodel.com/artificial-turf/california/porter-ranch/91326"
        className="text-primary underline"
      >
        artificial turf
      </a> for low-maintenance greenery, and elegant hardscape that connects every outdoor zone with purpose and style.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Project Highlights
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The original backyard was functional but lacked clear zoning, shade, and luxury features. Our team created an integrated plan that positions each space for maximum usability and aesthetic coherence.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Features like a seamless pool-to-deck transition, spa water features, artificial turf lounging zones, and premium pavers give this backyard a unified, resort feel.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Design Strategy
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      A clear design strategy focused on balancing luxury finishes with practical outdoor living needs. Elements like integrated lighting, durable hardscape, and lush artificial turf were chosen to create flow between lounging, swimming, and entertaining spaces.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Natural tones and high-quality materials were selected to complement the home’s architecture in Porter Ranch, while enhancing every outdoor living experience. To explore more projects like this, see our <a
        href="https://modernbuildremodel.com/outdoor-living/california/porter-ranch/91326"
        className="text-primary underline"
      >
        Outdoor Living
      </a> services.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      The Experience
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      This backyard now feels like an outdoor living room and resort, with areas dedicated to play, relaxation, and social connection. Poolside seating, spa jets, shade structures, and green zones combine for a continuous experience.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Whether hosting evening gatherings or enjoying quiet mornings by the water, this project demonstrates how outdoor living spaces can elevate daily life.
    </p>

  </div>
</section>


      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
